import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTenantPath } from '../lib/tenantPath';
import axios from 'axios';
import {
  ArrowLeft, ArrowRight, FileText, FilePdf, Play, Hash, Tag, CurrencyInr, Image as ImageIcon,
  Cube, ArrowSquareOut, PencilSimple, FloppyDisk, X, Trash, Plus
} from '@phosphor-icons/react';
import useAuthStore from '../store/authStore';
import { confirm } from '../components/ConfirmDialog';
import { fileUrl } from '../lib/fileUrl';

import { notify } from '../lib/notify';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EDITABLE_ROLES = ['Vice President', 'President'];

const SpecRow = ({ icon: Icon, label, value }) => (
  <div className="p-3 bg-white border border-[#D5E8E4] rounded-lg">
    <div className="flex items-center gap-2 text-xs text-[#6D8E87] mb-0.5">
      {Icon && <Icon size={14} />}
      {label}
    </div>
    <div className="text-sm font-semibold text-[#052A23] truncate">{value || '—'}</div>
  </div>
);

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const tp = useTenantPath();
  const { user } = useAuthStore();
  const canEdit = EDITABLE_ROLES.includes(user?.role);

  const [product, setProduct] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  // Feb 2026 book-style navigation — hold the visible product list so we can
  // jump to prev/next without going back to the index page. Ordering matches
  // what Products.js renders (created_at desc — the backend default sort).
  // Fetched once on mount then cached in-component; if a user opens the
  // detail page from a deep link (no list context), we still get the list
  // via the same API call.
  const [productList, setProductList] = useState([]);

  // Edit mode
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${API}/products/${id}`);
      setProduct(r.data); setActiveImageIndex(0);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load product');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  // Fetch the sibling list once — used to compute prev/next.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/products`).then((r) => {
      if (!cancelled) setProductList(Array.isArray(r.data) ? r.data : []);
    }).catch(() => { /* fine — nav just won't render */ });
    return () => { cancelled = true; };
  }, []);

  // Compute the prev/next product IDs based on the fetched list.
  const currentIndex = productList.findIndex(p => p.id === id);
  const prevProduct = currentIndex > 0 ? productList[currentIndex - 1] : null;
  const nextProduct = currentIndex >= 0 && currentIndex < productList.length - 1
    ? productList[currentIndex + 1]
    : null;

  const gotoProduct = (target) => {
    if (!target) return;
    // Reset image carousel + scroll to top so it feels like turning a page.
    setActiveImageIndex(0);
    window.scrollTo({ top: 0, behavior: 'instant' });
    navigate(tp(`/products/${target.id}`));
  };

  // Keyboard shortcuts — ← / → to move between products. Skip when the user
  // is typing in a form field (editing mode) so we don't hijack their input.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || '';
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return;
      if (editing) return;
      if (e.key === 'ArrowLeft' && prevProduct) { e.preventDefault(); gotoProduct(prevProduct); }
      else if (e.key === 'ArrowRight' && nextProduct) { e.preventDefault(); gotoProduct(nextProduct); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prevProduct?.id, nextProduct?.id, editing]);

  const beginEdit = () => {
    setForm({
      name: product.name || '',
      description: product.description || '',
      price: product.price ?? '',
      category: product.category || '',
      hsn_code: product.hsn_code || '',
      product_code: product.product_code || '',
      sku_code: product.sku_code || '',
      tax_bracket: product.tax_bracket ?? 18,
      video_link: product.video_link || '',
      cogs_price: product.cogs_price ?? '',
    });
    setSaveError(null);
    setEditing(true);
  };
  const cancelEdit = () => { setEditing(false); setSaveError(null); };

  const handleSave = async () => {
    setSaving(true); setSaveError(null);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        price: form.price === '' ? 0 : Number(form.price),
        category: form.category,
        hsn_code: form.hsn_code,
        product_code: form.product_code,
        sku_code: form.sku_code,
        tax_bracket: form.tax_bracket === '' ? 18 : Number(form.tax_bracket),
        video_link: form.video_link,
      };
      // Only VP/President can edit COGS. Skip the field entirely for anyone else so we
      // don't accidentally overwrite it to 0 for the (currently non-existent) case of a
      // non-editor hitting this form.
      if (canEdit) {
        payload.cogs_price = form.cogs_price === '' ? 0 : Number(form.cogs_price);
      }
      const r = await axios.patch(`${API}/products/${id}`, payload);
      setProduct(r.data.product);
      setEditing(false);
      if (r.data.backfilled_quotation_items > 0) {
        // Confirmation dialog is overkill here — quick alert is enough for a one-off signal.
        // eslint-disable-next-line no-alert
        notify(`COGS updated. ${r.data.backfilled_quotation_items} historical quotation line${r.data.backfilled_quotation_items === 1 ? '' : 's'} backfilled with the new cost.`);
      }
    } catch (e) {
      setSaveError(e.response?.data?.detail || 'Failed to save. Please try again.');
    } finally { setSaving(false); }
  };

  const handleDeleteMedia = async (mediaPath, fileType, label) => {
    const ok = await confirm({
      title: `Delete this ${label}?`,
      message: `This will remove the ${label} from the product. The original file will be retained in storage but no longer appear here. Continue?`,
      confirmText: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/products/${id}/media`, { params: { file_path: mediaPath, file_type: fileType } });
      await load();
      setActiveImageIndex(0);
    } catch (e) {
      notify(e.response?.data?.detail || `Failed to delete ${label}`);
    }
  };

  if (loading) return <div className="p-8 text-[#00a69c]" data-testid="product-detail-loading">Loading product…</div>;
  if (error || !product) {
    return (
      <div className="p-8 max-w-xl" data-testid="product-detail-error">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#3B5A54] hover:text-[#00a69c] mb-4">
          <ArrowLeft size={18} /> Back
        </button>
        <div className="p-4 bg-rose-50 border border-rose-200 rounded-lg text-rose-700 text-sm">
          {error || 'Product not found.'}
        </div>
      </div>
    );
  }

  const images = product.images || [];
  const videos = product.videos || [];
  const catalogues = product.catalogues || product.files || [];
  const heroSrc = images.length > 0 ? fileUrl(images[activeImageIndex]) : null;
  const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="product-detail-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#3B5A54] hover:text-[#00a69c] transition"
          data-testid="product-back-btn"
        >
          <ArrowLeft size={18} /> Back to Products
        </button>

        {/* Feb 2026 book-style navigation. Buttons are always rendered so the
            layout doesn't jitter; they disable at either end of the list.
            Keyboard: ← / → also work anywhere on the page. */}
        {productList.length > 1 && currentIndex >= 0 && (
          <div className="flex items-center gap-2" data-testid="product-nav-bar">
            <button
              onClick={() => gotoProduct(prevProduct)}
              disabled={!prevProduct}
              className={`inline-flex items-center gap-1 px-3 py-1.5 border rounded-lg text-sm font-medium transition ${
                prevProduct
                  ? 'border-[#00a69c] text-[#00a69c] hover:bg-[#E2F3F0]'
                  : 'border-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed'
              }`}
              data-testid="product-nav-prev-btn"
              title={prevProduct ? `Previous: ${prevProduct.name}` : 'This is the first product'}
            >
              <ArrowLeft size={16} /> Previous
            </button>
            <span className="text-xs text-[#6D8E87] font-medium tabular-nums px-1" data-testid="product-nav-counter">
              {currentIndex + 1} / {productList.length}
            </span>
            <button
              onClick={() => gotoProduct(nextProduct)}
              disabled={!nextProduct}
              className={`inline-flex items-center gap-1 px-3 py-1.5 border rounded-lg text-sm font-medium transition ${
                nextProduct
                  ? 'border-[#00a69c] text-[#00a69c] hover:bg-[#E2F3F0]'
                  : 'border-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed'
              }`}
              data-testid="product-nav-next-btn"
              title={nextProduct ? `Next: ${nextProduct.name}` : 'This is the last product'}
            >
              Next <ArrowRight size={16} />
            </button>
          </div>
        )}

        {canEdit && !editing && (
          <button
            onClick={beginEdit}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#00a69c] text-white rounded-lg text-sm font-medium hover:bg-[#008c84] transition"
            data-testid="product-edit-btn"
          >
            <PencilSimple size={16} weight="bold" /> Edit product
          </button>
        )}
        {canEdit && editing && (
          <div className="flex items-center gap-2">
            <button
              onClick={cancelEdit}
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#D5E8E4] rounded-lg text-sm font-medium text-[#3B5A54] hover:bg-[#F4F9F7] transition"
              data-testid="product-edit-cancel-btn"
              disabled={saving}
            >
              <X size={16} /> Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#00a69c] text-white rounded-lg text-sm font-medium hover:bg-[#008c84] transition disabled:opacity-50"
              data-testid="product-edit-save-btn"
            >
              <FloppyDisk size={16} weight="bold" /> {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        )}
      </div>

      {saveError && (
        <div className="mb-4 p-3 rounded-md bg-rose-50 border border-rose-200 text-sm text-rose-700" data-testid="product-edit-error">
          {saveError}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Media column */}
        <div>
          <div className="bg-white border border-[#D5E8E4] rounded-2xl overflow-hidden">
            <div className="h-72 sm:h-96 bg-[#E2F3F0] flex items-center justify-center relative" data-testid="product-hero">
              {heroSrc ? (
                <>
                  <img src={heroSrc} alt={product.name} className="w-full h-full object-contain" />
                  {canEdit && (
                    <button
                      onClick={() => handleDeleteMedia(images[activeImageIndex], 'image', 'image')}
                      className="absolute top-2 right-2 p-1.5 rounded-md bg-white/90 text-rose-600 hover:bg-rose-50 shadow"
                      title="Delete this image"
                      data-testid={`delete-image-${activeImageIndex}`}
                    >
                      <Trash size={16} weight="bold" />
                    </button>
                  )}
                </>
              ) : (
                <div className="text-center text-[#6D8E87]">
                  <ImageIcon size={48} className="mx-auto mb-2 opacity-60" />
                  <p className="text-sm">No images uploaded</p>
                </div>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 p-3 overflow-x-auto" data-testid="product-thumbs">
                {images.map((img, i) => (
                  <button
                    key={img}
                    onClick={() => setActiveImageIndex(i)}
                    className={`shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition ${
                      i === activeImageIndex ? 'border-[#00a69c]' : 'border-transparent hover:border-[#B5D9D2]'
                    }`}
                    data-testid={`product-thumb-${i}`}
                  >
                    <img src={fileUrl(img)} alt={`thumb-${i}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {product.video_link && !editing && (
            <a
              href={product.video_link}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#E2F3F0] text-[#00a69c] rounded-lg text-sm font-medium hover:bg-[#B5D9D2] transition"
              data-testid="product-video-link"
            >
              <Play size={16} weight="fill" /> Watch product video <ArrowSquareOut size={14} />
            </a>
          )}

          {videos.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="product-videos-section">
              <h3 className="text-sm font-semibold text-[#052A23]">Videos ({videos.length})</h3>
              {videos.map((v, i) => (
                <div key={v} className="relative">
                  <video controls src={fileUrl(v)} className="w-full rounded-lg border border-[#D5E8E4]" />
                  {canEdit && (
                    <button
                      onClick={() => handleDeleteMedia(v, 'video', 'video')}
                      className="absolute top-2 right-2 p-1.5 rounded-md bg-white/90 text-rose-600 hover:bg-rose-50 shadow"
                      title="Delete this video"
                      data-testid={`delete-video-${i}`}
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={() => navigate(tp('/products'))}
            className="mt-4 inline-flex items-center gap-2 text-sm text-[#00a69c] hover:underline"
            data-testid="upload-more-link"
          >
            <Plus size={14} /> Use the Products page to upload more images / videos / PDFs
          </button>
        </div>

        {/* Details / Edit form column */}
        <div>
          {!editing ? (
            <>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-[#6D8E87] mb-1">{product.category || 'Uncategorised'}</p>
                  <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }} data-testid="product-name">
                    {product.name}
                  </h1>
                </div>
                {product.status === 'approved' && (
                  <span className="px-2 py-0.5 bg-[#D1FAE5] text-[#065F46] text-xs rounded-full font-semibold">APPROVED</span>
                )}
                {product.status === 'pending' && (
                  <span className="px-2 py-0.5 bg-[#FEF3C7] text-[#92400E] text-xs rounded-full font-semibold">PENDING</span>
                )}
              </div>

              <div className="flex items-baseline gap-2 mb-5">
                <span className="text-3xl font-bold text-[#00a69c]" style={{ fontFamily: 'Outfit' }} data-testid="product-price">
                  {formatINR(product.price)}
                </span>
                {product.tax_bracket !== undefined && product.tax_bracket !== null && (
                  <span className="text-sm text-[#6D8E87]">+ {product.tax_bracket}% GST</span>
                )}
              </div>

              {product.description && (
                <div className="mb-5">
                  <h3 className="text-sm font-semibold text-[#052A23] mb-1">Description</h3>
                  <p className="text-sm text-[#3B5A54] whitespace-pre-wrap leading-relaxed" data-testid="product-description">
                    {product.description}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 mb-5" data-testid="product-specs">
                <SpecRow icon={Hash} label="Product Code" value={product.product_code} />
                <SpecRow icon={Tag} label="SKU" value={product.sku_code} />
                <SpecRow icon={FileText} label="HSN Code" value={product.hsn_code} />
                <SpecRow icon={CurrencyInr} label="GST %" value={product.tax_bracket != null ? `${product.tax_bracket}%` : null} />
                <SpecRow icon={Cube} label="Category" value={product.category} />
                <SpecRow icon={Tag} label="Status" value={product.status} />
              </div>

              {/* Executive-only: raw cost (COGS) + calculated gross margin. Sales roles never see this. */}
              {canEdit && (
                <div className="mb-5 p-4 rounded-xl border border-[#F59E0B]/30 bg-gradient-to-br from-[#FFFBEB] to-[#FEF3C7]" data-testid="cogs-panel">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-[#92400E] font-bold">Executive View</p>
                      <h3 className="text-sm font-semibold text-[#052A23]">Cost &amp; Margin</h3>
                    </div>
                    <span className="text-[10px] text-[#92400E] bg-white/60 px-2 py-1 rounded-full font-semibold">VP / President</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-2 bg-white/80 rounded-lg" data-testid="cogs-value">
                      <div className="text-[10px] text-[#92400E] uppercase tracking-wide mb-0.5">COGS</div>
                      <div className="text-base font-bold text-[#052A23]">{Number(product.cogs_price) > 0 ? formatINR(product.cogs_price) : '—'}</div>
                    </div>
                    <div className="p-2 bg-white/80 rounded-lg" data-testid="cogs-gm-amount">
                      <div className="text-[10px] text-[#92400E] uppercase tracking-wide mb-0.5">GM ₹</div>
                      <div className="text-base font-bold text-[#00a69c]">
                        {Number(product.cogs_price) > 0 && Number(product.price) > 0
                          ? formatINR(Number(product.price) - Number(product.cogs_price))
                          : '—'}
                      </div>
                    </div>
                    <div className="p-2 bg-white/80 rounded-lg" data-testid="cogs-gm-pct">
                      <div className="text-[10px] text-[#92400E] uppercase tracking-wide mb-0.5">GM %</div>
                      <div className="text-base font-bold text-[#00a69c]">
                        {Number(product.cogs_price) > 0 && Number(product.price) > 0
                          ? `${(((Number(product.price) - Number(product.cogs_price)) / Number(product.price)) * 100).toFixed(1)}%`
                          : '—'}
                      </div>
                    </div>
                  </div>
                  {Number(product.cogs_price) === 0 && (
                    <p className="text-[11px] text-[#92400E] mt-2">No COGS set yet — edit this product to unlock gross-margin tracking.</p>
                  )}
                </div>
              )}
            </>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-3" data-testid="product-edit-form">
              <EditField label="Name *" testid="edit-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
              <EditField label="Category" testid="edit-category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} />
              <div className="grid grid-cols-2 gap-3">
                <EditField label="Price (₹)" testid="edit-price" type="number" min="0" step="0.01" value={form.price} onChange={(v) => setForm({ ...form, price: v })} />
                <EditField label="GST %" testid="edit-tax" type="number" min="0" max="100" step="0.01" value={form.tax_bracket} onChange={(v) => setForm({ ...form, tax_bracket: v })} />
              </div>
              {canEdit && (
                <div className="p-3 rounded-lg border border-[#F59E0B]/30 bg-[#FFFBEB]" data-testid="cogs-edit-panel">
                  <label className="block text-xs font-semibold text-[#92400E] mb-1 uppercase tracking-wide">Cost of Goods Sold (COGS) — Executive Only</label>
                  <input
                    type="number" min="0" step="0.01"
                    value={form.cogs_price}
                    onChange={(e) => setForm({ ...form, cogs_price: e.target.value })}
                    placeholder="e.g. 50000"
                    className="w-full px-3 py-2 border border-[#F59E0B]/40 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/30 focus:border-[#F59E0B] bg-white"
                    data-testid="edit-cogs"
                  />
                  <p className="text-[11px] text-[#92400E] mt-1">
                    Updating this value backfills gross margin on historical quotations
                    that were captured before COGS existed (cogs_at_sale == 0). Sold quotes
                    with an existing COGS snapshot are never touched.
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <EditField label="Product Code" testid="edit-product-code" value={form.product_code} onChange={(v) => setForm({ ...form, product_code: v })} />
                <EditField label="SKU Code" testid="edit-sku" value={form.sku_code} onChange={(v) => setForm({ ...form, sku_code: v })} />
              </div>
              <EditField label="HSN Code" testid="edit-hsn" value={form.hsn_code} onChange={(v) => setForm({ ...form, hsn_code: v })} />
              <EditField label="Video link (YouTube / Vimeo URL)" testid="edit-video-link" value={form.video_link} onChange={(v) => setForm({ ...form, video_link: v })} />
              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-1">Description</label>
                <textarea
                  rows={4}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 border border-[#D5E8E4] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#00a69c]/30 focus:border-[#00a69c]"
                  data-testid="edit-description"
                />
              </div>
            </form>
          )}

          {/* Catalogues */}
          {catalogues.length > 0 && (
            <div className="mb-5" data-testid="product-catalogues">
              <h3 className="text-sm font-semibold text-[#052A23] mb-2">Catalogues ({catalogues.length})</h3>
              <ul className="space-y-2">
                {catalogues.map((c, i) => (
                  <li key={c} className="flex items-center gap-2">
                    <a
                      href={fileUrl(c)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center gap-2 px-3 py-2 bg-white border border-[#D5E8E4] rounded-lg text-sm text-[#00a69c] hover:bg-[#F4F9F7] transition"
                      data-testid={`product-catalogue-${i}`}
                    >
                      <FilePdf size={18} weight="fill" className="text-rose-600" />
                      <span className="flex-1 truncate">Document {i + 1}</span>
                      <ArrowSquareOut size={14} />
                    </a>
                    {canEdit && (
                      <button
                        onClick={() => handleDeleteMedia(c, 'catalogue', 'catalogue')}
                        className="p-2 rounded-md text-rose-600 hover:bg-rose-50 transition"
                        title="Delete this catalogue"
                        data-testid={`delete-catalogue-${i}`}
                      >
                        <Trash size={16} weight="bold" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Link to={tp("/products")} className="inline-flex items-center gap-2 text-sm text-[#00a69c] hover:underline" data-testid="product-back-link">
            <ArrowLeft size={14} /> All products
          </Link>
        </div>
      </div>
    </div>
  );
};

const EditField = ({ label, value, onChange, testid, type = 'text', ...rest }) => (
  <div>
    <label className="block text-sm font-medium text-[#3B5A54] mb-1">{label}</label>
    <input
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-[#D5E8E4] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#00a69c]/30 focus:border-[#00a69c]"
      data-testid={testid}
      {...rest}
    />
  </div>
);

export default ProductDetail;
